import type { PointArray } from '../types/contour-definition-array-type';

export function interpolateGridPoints2D(
  points: PointArray,
  resolution: number,
): PointArray {
  if (points.length < 2) return points;

  const gridStep = 1 / resolution; // grid step from resolution
  const snapToGrid = (value: number): number =>
    Math.round(value * resolution) / resolution;

  // Adjust the start and end points to the nearest grid points
  const start = {
    x: snapToGrid(points[0].x),
    y: snapToGrid(points[0].y),
  };
  const end = {
    x: snapToGrid(points[points.length - 1].x),
    y: snapToGrid(points[points.length - 1].y),
  };

  const result: PointArray = [start]; // Start with the adjusted start point

  for (let i = 0; i < points.length - 1; i++) {
    const segmentStart = i === 0 ? start : points[i]; // Use adjusted start for the first segment
    const segmentEnd = i === points.length - 2 ? end : points[i + 1]; // Use adjusted end for the last segment

    // Calculate steps based on grid resolution
    const dx = segmentEnd.x - segmentStart.x;
    const dy = segmentEnd.y - segmentStart.y;
    const distance = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.ceil(distance / gridStep));

    // Add interpolated points
    for (let step = 1; step <= steps; step++) {
      const t = step / steps;
      const x = snapToGrid(segmentStart.x + dx * t);
      const y = snapToGrid(segmentStart.y + dy * t);

      // Avoid duplicates
      if (
        !result.length
        || result[result.length - 1].x !== x
        || result[result.length - 1].y !== y
      ) {
        result.push({ x, y });
      }
    }
  }

  return result;
}
